// Purpose: Reuse one event-scoped Promotion Idea for each saved public record or listing page.
// Features: Fixed collection allowlist, canonical Social Promotion entitlement, and public-only source projection.
// Dependencies: Firestore transactions, event add-on helpers, and shared image alias normalization.
// Important behavior: Repeated handoffs preserve authored Ideas. Listing drafts use event context,
// not child-record scans; no private inputs or external publishing are accepted.
import { doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { hasPurchasedAddOn } from "../../utils/event_addons";
import { getEntryImageUrls } from "../../utils/entry_images";
import { PROMOTION_SCOPES, PROMOTION_ENGINE_SOURCES } from "../config/promotion_settings_map";
import { PROMOTION_IDEA_STATUS } from "../constants/promotion_status";
import { PROMOTION_SOURCES as SOURCES, arePromotionResultsPublished } from "../constants/promotion_source_pages";

const validId = (value) => typeof value === "string" && value.trim() === value && value.length > 0 &&
  !value.includes("/") && !Array.from(value).some((character) => character.charCodeAt(0) < 32) &&
  ![".", "..", "_init", "_placeholder"].includes(value) && !/^__.*__$/.test(value);
const publicText = (value) => typeof value === "string" ? value.trim() : "";

export const getSourcePromotionIdea = async ({ eventId, source, sourceId, sourceScope = "record" }) => {
  const isCollection = sourceScope === "collection";
  if (!validId(eventId) || (!isCollection && !validId(sourceId)) || !["record", "collection"].includes(sourceScope) || !Object.prototype.hasOwnProperty.call(SOURCES, source) || (isCollection && sourceId)) {
    throw new Error("A valid event, source type, and saved record are required.");
  }
  const definition = SOURCES[source];
  if ((isCollection && !definition.page) || (!isCollection && definition.collectionOnly)) throw new Error("This promotion source scope is unsupported.");
  // Tuple encoding prevents event/source/record collisions while keeping document IDs path-safe.
  const id = `source_${encodeURIComponent(JSON.stringify([eventId, source, isCollection ? { scope: "collection" } : sourceId]))}`;
  if (id.length > 1500) throw new Error("The source identity is too long for a Promotion Idea.");
  const ideaRef = doc(db, "promotion_ideas", id);
  return runTransaction(db, async (transaction) => {
    const event = await transaction.get(doc(db, "events", eventId));
    const record = isCollection ? null : await transaction.get(doc(db, "events", eventId, definition.collection, sourceId));
    const existing = await transaction.get(ideaRef);
    const resultSettings = source === "results" ? await transaction.get(doc(db, "events", eventId, "settings", "voting")) : null;
    if (!event.exists() || !hasPurchasedAddOn(event.data(), "social")) {
      throw new Error("Social Promotion is not enabled for this event.");
    }
    // Listing-page drafts use event context, never private/draft child records.
    // Only canonical purchased module flags may enable this page handoff.
    if ((isCollection || source === "tickets") && definition.addon && !hasPurchasedAddOn({ purchasedAddOns: event.data().purchasedAddOns }, definition.addon)) {
      throw new Error("This source Power Up is not enabled for this event.");
    }
    if (source === "results" && (!resultSettings.exists() || !arePromotionResultsPublished(resultSettings.data()))) {
      throw new Error("Results must be released to voters before creating a public promotion.");
    }
    if (!isCollection && (!record.exists() || record.data().placeholder === true || record.data().init === true)) {
      throw new Error("The saved promotion source is unavailable.");
    }
    const data = isCollection ? event.data() : record.data();
    if (data.eventId != null && data.eventId !== eventId) throw new Error("The promotion source belongs to another event.");
    if (existing.exists()) {
      if (existing.data().eventId !== eventId) throw new Error("The Promotion Idea belongs to another event.");
      return { ...existing.data(), id };
    }
    const eventName = publicText(event.data().name) || publicText(event.data().title) || "this event";
    const pageUrl = isCollection && publicText(data.slug) ? `https://letsgovote.app/${encodeURIComponent(data.slug)}/${definition.page}` : "";
    const title = isCollection ? `${definition.pageLabel} — ${eventName}` : publicText(data.title) || publicText(data.name) || publicText(data.description) || definition.label;
    const pageInstruction = source === "event" ? `Create an announcement or reminder for ${eventName}.`
      : source === "volunteers" ? `Invite people to volunteer at ${eventName}. Do not invent available roles or shifts.`
      : source === "entries" ? `Spotlight the entries at ${eventName}. Do not invent entry details.`
      : source === "results" ? `Celebrate the published results for ${eventName} and thank participants, voters, volunteers and supporters. Link to the published winners; do not invent winner names, rankings or scores.`
      : `Promote ${definition.pageLabel?.toLowerCase()} for ${eventName}.`;
    const description = isCollection
      ? `${pageInstruction} Invite people to explore the event's ${definition.pageLabel.toLowerCase()} page.${pageUrl ? `\nPublic page: ${pageUrl}` : ""}`
      : publicText(data.description);
    const imageUrls = getEntryImageUrls({
      ...data,
      imageUrls: [...getEntryImageUrls(data), ...(isCollection ? [data.eventImage, data.eventLogo] : ["vendors", "sponsors"].includes(source) ? [data.logoUrl] : [])],
    });
    const idea = {
      title, description, imageUrls,
      relatedModule: source, relatedId: isCollection ? "" : sourceId, relatedName: isCollection ? definition.pageLabel : publicText(data.name) || title,
      ...(isCollection ? { relatedScope: "collection" } : {}),
      eventId, eventType: publicText(event.data().eventType), scope: PROMOTION_SCOPES.EVENT,
      requestType: "social_post", category: definition.category, platforms: [], postTypes: [],
      engineSource: definition.engineSource || PROMOTION_ENGINE_SOURCES.MANUAL,
      ideaStatus: PROMOTION_IDEA_STATUS.NEW, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    };
    transaction.set(ideaRef, idea);
    return { ...idea, id };
  });
};
